import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Fade,
  ClickAwayListener,
  Popper,
  Paper,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import BoltIcon from "@mui/icons-material/Bolt";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { scaleUp } from "./animations";

// Quick Actions data (icons only, color comes from theme)
const quickActionsData = [
  { id: 1, label: "Add New Lead", icon: PersonAddOutlinedIcon },
  { id: 2, label: "Schedule Follow-up", icon: EventNoteOutlinedIcon },
  { id: 3, label: "Add Call Log", icon: PhoneOutlinedIcon },
  { id: 4, label: "Send WhatsApp", icon: WhatsAppIcon },
  { id: 5, label: "Create Task", icon: AssignmentOutlinedIcon },
  { id: 6, label: "Generate Report", icon: AssessmentOutlinedIcon },
];

const QuickActionsDropdown = ({ onActionClick }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleActionClick = (action) => {
    if (onActionClick) {
      onActionClick(action);
    }
    handleClose();
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box>
        <Button
          variant="contained"
          startIcon={<BoltIcon sx={{ fontSize: 16 }} />}
          onClick={handleClick}
          sx={{
            backgroundColor: theme.palette.primary.main,
            borderRadius: 2,
            px: 2,
            py: 0.875,
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            boxShadow: "none",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              transform: "translateY(-1px)",
            },
          }}
        >
          Quick Actions
        </Button>

        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-end"
          transition
          sx={{ zIndex: 1300 }}
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <Paper
                elevation={8}
                sx={{
                  width: 390,
                  maxWidth: "calc(100vw - 32px)",
                  borderRadius: "16px",
                  overflow: "hidden",
                  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2.5,
                    py: 2,
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 700,
                      // bgcolor: "primary.main",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Quick Actions
                  </Typography>
                  <IconButton
                    onClick={handleClose}
                    sx={{
                      p: 0.5,
                      color: "#94a3b8",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "#f1f5f9",
                        color: "#64748b",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>

                {/* Actions Grid */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.5,
                    p: 1.5,
                  }}
                >
                  {quickActionsData.map((action, index) => {
                    const IconComponent = action.icon;
                    return (
                      <Box
                        key={action.id}
                        onClick={() => handleActionClick(action)}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          p: 2.5,
                          borderRadius: "12px",
                          cursor: "pointer",
                          backgroundColor: "#fff",
                          border: "1px solid #f1f5f9",
                          transition: "all 0.2s ease",
                          animation: open
                            ? `${scaleUp} 0.25s ease forwards`
                            : "none",
                          animationDelay: `${index * 0.04}s`,
                          opacity: open ? 0 : 1,
                          "&:hover": {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                            borderColor: alpha(theme.palette.primary.main, 0.2),
                            transform: "translateY(-2px)",
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                            "& .action-icon": {
                              transform: "scale(1.1)",
                            },
                          },
                        }}
                      >
                        <Box
                          className="action-icon"
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "40px",
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "transform 0.2s ease",
                          }}
                        >
                          <IconComponent
                            sx={{
                              fontSize: 20,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#334155",
                            textAlign: "center",
                            lineHeight: 1.3,
                          }}
                        >
                          {action.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default QuickActionsDropdown;
